import { useState } from "react";
import { motion } from "framer-motion";
import { Wand2, Download, Loader2, Sparkles } from "lucide-react";
import { Input } from "@/components/ui/input";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fadeUp = { hidden: { opacity: 0, y: 20 }, visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.5 } }) };

const STYLES = [
  { id: "luxury", label: "Luxury Gold" },
  { id: "modern", label: "Modern Minimal" },
  { id: "tropical", label: "Tropical Caribbean" },
  { id: "cinematic", label: "Cinematic Dark" },
  { id: "elegant", label: "Elegant White" },
];

export default function PosterGeneratorPage() {
  const [description, setDescription] = useState("");
  const [style, setStyle] = useState("luxury");
  const [loading, setLoading] = useState(false);
  const [image, setImage] = useState(null);

  const generate = async () => {
    if (!description.trim()) { toast.error("Describe your event or poster."); return; }
    setLoading(true);
    setImage(null);
    try {
      const res = await axios.post(`${API}/ai/generate-poster`, { description, style }, { timeout: 120000 });
      setImage(res.data.image_base64);
      toast.success("Poster generated!");
    } catch { toast.error("Generation failed. Please try again."); } finally { setLoading(false); }
  };

  const download = () => {
    if (!image) return;
    const link = document.createElement("a");
    link.href = `data:image/png;base64,${image}`;
    link.download = `poster-${Date.now()}.png`;
    link.click();
  };

  return (
    <div data-testid="poster-generator-page" className="pt-20 min-h-screen bg-luxury-black">
      <section className="py-24 md:py-28">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" animate="visible" className="text-center mb-12">
            <motion.p variants={fadeUp} custom={0} className="text-xs font-body font-medium uppercase tracking-[0.3em] text-gold mb-4">AI Creative Studio</motion.p>
            <motion.h1 variants={fadeUp} custom={1} className="font-heading text-4xl sm:text-5xl font-light text-white mb-4">
              Event Poster <span className="gold-gradient-text">Generator</span>
            </motion.h1>
            <motion.p variants={fadeUp} custom={2} className="text-white/50 font-body text-base max-w-lg mx-auto">
              Describe your event and our AI will create a stunning professional poster in seconds.
            </motion.p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Controls */}
            <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={3} className="glass-card p-8 space-y-6">
              <div>
                <label className="text-white/50 text-xs font-body uppercase tracking-wider mb-2 block">Describe Your Event *</label>
                <textarea data-testid="poster-description" value={description} onChange={e => setDescription(e.target.value)} placeholder="e.g., Caribbean yacht party, New Year's Eve, sunset, champagne, live DJ, 200 VIP guests..." rows={4} className="w-full bg-white/5 border border-white/10 text-white text-sm px-4 py-3 rounded-sm focus:outline-none focus:border-gold/50 placeholder:text-white/30 font-body resize-none" />
              </div>
              <div>
                <label className="text-white/50 text-xs font-body uppercase tracking-wider mb-3 block">Style</label>
                <div className="flex flex-wrap gap-2">
                  {STYLES.map(s => (
                    <button key={s.id} data-testid={`style-${s.id}`} onClick={() => setStyle(s.id)} className={`px-4 py-2 text-xs font-body transition-all border ${style === s.id ? "border-gold bg-gold/10 text-gold" : "border-white/10 text-white/40 hover:border-white/20"}`}>
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
              <button onClick={generate} disabled={loading} data-testid="generate-poster-btn" className="w-full flex items-center justify-center gap-2 bg-gold text-black font-body font-medium text-sm py-4 hover:bg-gold-light transition-colors disabled:opacity-50">
                {loading ? <><Loader2 size={16} className="animate-spin" /> Generating (30-60s)...</> : <><Wand2 size={16} /> Generate Poster</>}
              </button>
            </motion.div>

            {/* Preview */}
            <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={4} className="glass-card p-8 flex items-center justify-center min-h-[400px]">
              {loading ? (
                <div className="text-center">
                  <Sparkles size={32} className="text-gold mx-auto mb-4 animate-pulse" />
                  <p className="text-white/40 font-body text-sm">AI is crafting your poster...</p>
                  <p className="text-white/20 font-body text-xs mt-2">This takes 30-60 seconds</p>
                </div>
              ) : image ? (
                <div className="space-y-4">
                  <img src={`data:image/png;base64,${image}`} alt="Generated poster" className="max-w-full max-h-[500px] mx-auto" data-testid="generated-poster" />
                  <button onClick={download} data-testid="download-poster-btn" className="w-full flex items-center justify-center gap-2 border border-gold/30 text-gold font-body text-sm py-3 hover:bg-gold/10 transition-colors">
                    <Download size={14} /> Download Poster
                  </button>
                </div>
              ) : (
                <div className="text-center">
                  <Wand2 size={32} className="text-white/10 mx-auto mb-3" />
                  <p className="text-white/20 font-body text-sm">Your poster will appear here</p>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
